import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { TeamCard } from '../../team-card';
import type { AtlassianTeamUpdateArchiveHydratedAction } from '../../types';
import { CardFooter } from '../card-footer';
import messages from '../messages';

const styles = cssMap({
	content: {
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type ArchiveTeamsCardTeamViewProps = {
	team: AtlassianTeamUpdateArchiveHydratedAction;
	onBack: () => void;
	onRemove: (team: AtlassianTeamUpdateArchiveHydratedAction) => Promise<void>;
	onArchive: (teams: AtlassianTeamUpdateArchiveHydratedAction[]) => void;
	cloudId: string;
	isReadonly?: boolean;
};

export const ArchiveTeamsCardTeamView = ({
	team,
	onBack,
	onRemove,
	onArchive,
	cloudId,
	isReadonly = false,
}: ArchiveTeamsCardTeamViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const teamCardActions = useMemo(
		() => [
			{
				children: formatMessage(messages.viewProfile),
				onClick: () => {
					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'viewTeamProfileCleanUpTeams',
						attributes: {
							teamId: team.data.teamId,
						},
					});
					const { onNavigate } = navigateToTeamsApp({
						type: 'TEAM',
						payload: { teamId: team.data.teamId },
						cloudId,
					});
					onNavigate();
				},
			},
			{
				children: formatMessage(messages.removeFromCleanUp),
				onClick: async () => await onRemove(team),
				isDisabled: isReadonly,
			},
		],
		[formatMessage, onRemove, team, cloudId, sendAnalyticsEvent, isReadonly],
	);

	return (
		<Box>
			<Box xcss={styles.content}>
				<Stack space="space.100">
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={ArrowLeftIcon}
						label={formatMessage(messages.backLabel)}
						onClick={() => {
							sendAnalyticsEvent({
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'closeTeamCardCleanUpTeams',
								attributes: {
									teamId: team.data.teamId,
								},
							});
							onBack();
						}}
					/>
					<TeamCard team={team} actions={teamCardActions} cloudId={cloudId} />
				</Stack>
			</Box>
			{!isReadonly && (
				<CardFooter>
					<Inline alignInline="end">
						<Button
							appearance="primary"
							onClick={() => onArchive([team])}
							iconBefore={ArchiveBoxIcon}
						>
							{formatMessage(messages.archiveButton, { teamCount: 1 })}
						</Button>
					</Inline>
				</CardFooter>
			)}
		</Box>
	);
};
